import { gql, useMutation } from '@apollo/client';
import IconClose from '@apollo/icons/default/IconClose.svg';
import {
  Avatar,
  Button,
  CardSection,
  CardSectionControl,
  CardSectionDescription,
  CardSectionTitle,
  Checkbox,
  Group,
  Heading,
  PopoverTrigger,
} from '@apollo/orbit';
import { debounce } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import {
  ReviewerMultiselectPopover,
  ReviewerMultiselectPopoverUsersServiceFragment,
} from 'src/app/graph/proposalLayout/proposalHomepage/requestedReviewersSection/ReviewerMultiselectPopover';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { PlainHtmlButton } from 'src/components/common/plainHtmlButton/PlainHtmlButton';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';

export const DefaultReviewersSectionServiceFragment = gql`
  fragment DefaultReviewersSectionServiceFragment on Service {
    id
    defaultProposalReviewers {
      id
      ... on User {
        fullName
        avatarUrl
      }
    }
    proposalsMustBeApprovedByADefaultReviewer
    ...ReviewerMultiselectPopoverUsersServiceFragment
  }
  ${ReviewerMultiselectPopoverUsersServiceFragment}
`;

const MAX_VISIBLE_REVIEWERS = 3;

export const DefaultReviewersSection = ({
  graph,
}: {
  graph: GraphQLTypes.DefaultReviewersSectionServiceFragment;
}) => {
  const [shouldShowAllDefaultReviewers, setShouldShowAllDefaultReviewers] =
    useState(false);

  const shouldShowMandatoryDefaultReviewersCheckbox = useLDFlag(
    'nebula-proposals-ga',
  );
  const [
    mandatoryDefaultReviewersChecked,
    setMandatoryDefaultReviewersChecked,
  ] = useState(graph.proposalsMustBeApprovedByADefaultReviewer);

  const selectedMemberIds = useMemo(
    () =>
      graph.defaultProposalReviewers
        .map((reviewer) => reviewer?.id)
        .filter((id): id is NonNullable<typeof id> => !!id),
    [graph.defaultProposalReviewers],
  );

  const [setDefaultReviewers] = useMutation<
    GraphQLTypes.DefaultReviewersSectionSetDefaultReviewersMutation,
    GraphQLTypes.DefaultReviewersSectionSetDefaultReviewersMutationVariables
  >(gql`
    mutation DefaultReviewersSectionSetDefaultReviewersMutation(
      $graphId: ID!
      $input: SetProposalDefaultReviewersInput!
    ) {
      service(id: $graphId) {
        setProposalDefaultReviewers(input: $input) {
          ... on Service {
            id
            defaultProposalReviewers {
              id
              ... on User {
                fullName
                avatarUrl
              }
            }
          }
          ... on PermissionError {
            message
          }
          ... on ValidationError {
            message
          }
        }
      }
    }
  `);

  const [setMandatoryDefaultReviewers] = useMutation<
    GraphQLTypes.DefaultReviewersSectionSetMandatoryDefaultReviewersMutation,
    GraphQLTypes.DefaultReviewersSectionSetMandatoryDefaultReviewersMutationVariables
  >(gql`
    mutation DefaultReviewersSectionSetMandatoryDefaultReviewersMutation(
      $graphId: ID!
      $checked: Boolean!
    ) {
      service(id: $graphId) {
        proposalsMustBeApprovedByADefaultReviewer(mustBeApproved: $checked) {
          ... on Service {
            id
            proposalsMustBeApprovedByADefaultReviewer
          }
          ... on PermissionError {
            message
          }
          ... on ValidationError {
            message
          }
        }
      }
    }
  `);

  const onSelectDefaultReviewers = useCallback(
    (selectedIds: string[]) =>
      setDefaultReviewers({
        variables: {
          graphId: graph.id,
          input: {
            reviewerUserIds: selectedIds,
          },
        },
      }),
    [graph.id, setDefaultReviewers],
  );

  const debouncedSetMandatoryDefaultReviewers = useMemo(
    () =>
      debounce(async (nextValue: boolean) => {
        const { data: responseData } = await setMandatoryDefaultReviewers({
          variables: { graphId: graph.id, checked: nextValue },
        });
        const result =
          responseData?.service?.proposalsMustBeApprovedByADefaultReviewer;
        if (
          result?.__typename === 'PermissionError' ||
          result?.__typename === 'ValidationError'
        ) {
          setMandatoryDefaultReviewersChecked(!nextValue);
        }
      }, 300),
    [graph.id, setMandatoryDefaultReviewers],
  );
  const onCheckMandatoryDefaultReviewers = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextCheckedValue = e.target.ariaChecked === 'false';
      setMandatoryDefaultReviewersChecked(nextCheckedValue);
      debouncedSetMandatoryDefaultReviewers(nextCheckedValue);
    },
    [debouncedSetMandatoryDefaultReviewers],
  );

  return (
    <>
      <CardSection className="flex-row">
        <Group orientation="vertical">
          <CardSectionTitle>Default reviewers</CardSectionTitle>
          <CardSectionDescription>
            Assign teammates as default reviewers to automatically include them
            on every new proposal on this graph.
          </CardSectionDescription>
        </Group>
        <CardSectionControl>
          <ReviewerMultiselectPopover
            selectedMemberIds={selectedMemberIds}
            graph={graph}
            onClose={onSelectDefaultReviewers}
            trigger={
              <PermissionWarning
                graphId={graph.id}
                graphPermissions="canManageProposalPermissions"
                childRenderer={(hasPermission) => (
                  <WrapMaybe
                    on={hasPermission}
                    // Popover is passed a child by WrapMaybe that overrides the one passed here
                    // eslint-disable-next-line react/no-children-prop
                    element={<PopoverTrigger children={undefined} />}
                  >
                    <Button
                      variant="secondary"
                      className="mt-4 w-fit md:mt-0"
                      isDisabled={!hasPermission}
                    >
                      Manage default reviewers
                    </Button>
                  </WrapMaybe>
                )}
              />
            }
          />
        </CardSectionControl>
      </CardSection>
      {graph.defaultProposalReviewers.length > 0 && (
        <div>
          <Heading variant="title" className="mb-2">
            Selected reviewers
          </Heading>
          <div className="flex flex-wrap gap-2">
            {graph.defaultProposalReviewers
              .filter(
                (reviewer): reviewer is NonNullable<typeof reviewer> =>
                  !!reviewer,
              )
              .slice(
                0,
                shouldShowAllDefaultReviewers
                  ? graph.defaultProposalReviewers.length
                  : MAX_VISIBLE_REVIEWERS,
              )
              .map((reviewer) => (
                <PermissionWarning
                  key={reviewer.id}
                  graphId={graph.id}
                  graphPermissions="canManageProposalPermissions"
                  childRenderer={(hasPermission) => (
                    <PlainHtmlButton
                      disabled={!hasPermission}
                      className="group flex items-center rounded-full border border-primary px-3 py-2"
                      onClick={() =>
                        onSelectDefaultReviewers(
                          selectedMemberIds.filter((id) => id !== reviewer.id),
                        )
                      }
                    >
                      {reviewer.__typename === 'User' ? (
                        <>
                          <Avatar
                            size="sm"
                            className="mr-2"
                            name={reviewer.fullName}
                            src={reviewer.avatarUrl ?? undefined}
                          />
                          <div className="mr-2">{reviewer.fullName}</div>
                        </>
                      ) : (
                        <>
                          <Avatar size="sm" className="mr-2" />
                          <div className="mr-2">Unknown</div>
                        </>
                      )}

                      {hasPermission && (
                        <div className="group-focus-visible:focus-ring size-7 rounded">
                          <IconClose className="m-2 text-secondary" />
                        </div>
                      )}
                    </PlainHtmlButton>
                  )}
                />
              ))}
            {graph.defaultProposalReviewers.length > MAX_VISIBLE_REVIEWERS &&
              !shouldShowAllDefaultReviewers && (
                <ClickableText
                  className="focus-visible:focus-ring text-link"
                  onClick={() =>
                    setShouldShowAllDefaultReviewers((value) => !value)
                  }
                >
                  View{' '}
                  {graph.defaultProposalReviewers.length -
                    MAX_VISIBLE_REVIEWERS}{' '}
                  more
                </ClickableText>
              )}
          </div>

          {shouldShowMandatoryDefaultReviewersCheckbox && (
            <PermissionWarning
              graphId={graph.id}
              graphPermissions="canManageProposalPermissions"
              childRenderer={(hasPermission) => (
                <Checkbox
                  className="mt-6"
                  isDisabled={!hasPermission}
                  isChecked={mandatoryDefaultReviewersChecked}
                  onChange={(e) => onCheckMandatoryDefaultReviewers(e)}
                >
                  Require at least 1 default reviewer for all proposals on this
                  graph.
                </Checkbox>
              )}
            />
          )}
        </div>
      )}
    </>
  );
};
